import { Component, OnInit, inject, input, output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ConfigurationHandlerService } from '../core/configuration-handler.service';
import { SnackbarService } from '../core/snackbar.service';
import { ConfirmDialogComponent } from '../parts/dialogs/confirm-dialog/confirm-dialog.component';
import { SuccessSnackbarComponent } from '../parts/snackbars/success-snackbar/success-snackbar.component';
import { ProfileService } from '../profile/profile.service';
import {
    GrowInAnimation,
    GrowOutAnimation,
    CopierComponent,
} from '@exl-ng/mulo-common';
import {
    EsploroPersonPortalFieldTypes,
    ResearcherProfileActivation,
} from '../shared/interfaces/profile-activation.interface';
import { ProfileActivationService } from './profile-activation.service';
import { TranslateModule } from '@ngx-translate/core';
import { MatButton } from '@angular/material/button';

@Component({
    selector: 'esp-profile-activation',
    templateUrl: './profile-activation.component.html',
    styleUrls: ['./profile-activation.component.scss'],
    animations: [GrowInAnimation, GrowOutAnimation],
    imports: [MatButton, CopierComponent, TranslateModule],
})
export class ProfileActivationComponent implements OnInit {
    private matDialog = inject(MatDialog);
    profileActivationService = inject(ProfileActivationService);
    private configurationHandlerService = inject(ConfigurationHandlerService);
    private snackbarService = inject(SnackbarService);
    private profileService = inject(ProfileService);

    activateStatus: EsploroPersonPortalFieldTypes;
    hasSelfActivate: boolean;
    profileUrl: string;
    showActivationStatusWithNoAsstes: boolean;
    showPublicProfileUrl: boolean;
    isPublicPage: boolean;
    showProfileActivationStatus: boolean;

    EsploroPersonPortalFieldTypes = EsploroPersonPortalFieldTypes;

    readonly isPublicProfile = output<boolean>();

    currentActivateStatus = (status?: string) => {
        status = status ?? this.activateStatus;
        return EsploroPersonPortalFieldTypes[status];
    };

    readonly mode = input<'setting' | 'profile'>('profile');

    ngOnInit(): void {
        this.hasSelfActivate =
            this.configurationHandlerService.getCustomerParameter(
                'esploro_profile_self_activation',
            ) === 'true';
        this.isPublicPage = this.profileService.isPublicPage;
        this.getResearchActivationStatus();
    }

    getResearchActivationStatus() {
        this.profileActivationService
            .getResearchActivationStatus()
            .subscribe((data: ResearcherProfileActivation) => {
                this.handleActivationData(data);
            });
    }

    handleActivationData(data: ResearcherProfileActivation) {
        this.activateStatus = data?.activationStatus;
        this.profileUrl = data?.publicProfileUrl;
        this.showActivationStatusWithNoAsstes =
            data?.hasActiveProfileWithNoAssets;
        this.showPublicProfileUrl =
            data?.hasActiveProfileWithAssets ||
            this.currentActivateStatus() ===
                EsploroPersonPortalFieldTypes.DISPLAY_NO_ASSETS;
        this.isPublicProfile.emit(this.showPublicProfileUrl);
        this.checkActivationStatusForProfile();
    }

    checkActivationStatusForProfile() {
        const activateStatusData =
            EsploroPersonPortalFieldTypes[this.activateStatus];

        const statusMessage =
            activateStatusData === EsploroPersonPortalFieldTypes.FALSE ||
            activateStatusData === EsploroPersonPortalFieldTypes.REFUSED ||
            activateStatusData ===
                EsploroPersonPortalFieldTypes.RESEARCHER_REQUESTED_ACTIVATION;
        if (
            (this.showActivationStatusWithNoAsstes || statusMessage) &&
            this.activateStatus
        ) {
            this.showProfileActivationStatus = true;
        } else {
            this.showProfileActivationStatus = false;
        }
    }

    openActivateDialog() {
        const dialogData = this.getActivateDialogData();
        setTimeout(() => {
            this.matDialog
                .open(ConfirmDialogComponent, dialogData)
                .afterClosed()
                .subscribe((result) => {
                    if (result && result !== 'cancel') {
                        this.executeActiveProfile();
                    }
                });
        }, 0);
    }

    getActivateDialogData() {
        const message =
            'research.profile.activate.' +
            (this.hasSelfActivate ? 'self' : 'librarian') +
            '.activate.dialog.message';
        const confirm =
            'research.profile.activate.' +
            (this.hasSelfActivate ? 'self' : 'librarian') +
            '.activate.dialog.confirm';

        return {
            data: {
                title: '',
                message: message,
                actions: {
                    confirm: confirm,
                    cancel: 'research.profile.activate.self.activate.dialog.cancel',
                },
                actionFocus: 'confirm',
            },
        };
    }

    executeActiveProfile() {
        const status = this.hasSelfActivate
            ? EsploroPersonPortalFieldTypes.TRUE
            : EsploroPersonPortalFieldTypes.RESEARCHER_REQUESTED_ACTIVATION;
        this.activateStatus = undefined;
        this.profileActivationService
            .setResearchActivationStatus(status)
            .subscribe((data: ResearcherProfileActivation) => {
                this.handleActivationData(data);
                if (
                    this.hasSelfActivate &&
                    !this.showActivationStatusWithNoAsstes
                ) {
                    this.successUpdateActivation();
                }
            });
    }

    successUpdateActivation() {
        setTimeout(() => {
            this.showSuccessMessage();
        }, 500);
    }

    showSuccessMessage() {
        this.snackbarService.showComponent(
            SuccessSnackbarComponent,
            'research.profile.activate.status.message.action.active.true',
            7000,
        );
    }

    onLinkCopied() {
        // this.service.onLinkCopied();
    }
}
